import { ProblemIdentificationPage } from "@/components/test-cases/ProblemIdentificationPage";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { agentIdentifier } from "@/api";
import { AgentList } from "@/components/agents/AgentList";
import { AgentsPage } from "@/components/agents/AgentsPage";
import { BatchPageView } from "@/components/batch-test/BatchPageView";
import { BatchTestListPage } from "@/components/batch-test/BatchTestListPage";
import { ConversationsPage } from "@/components/conversations/ConversationsPage";
import { parseConversationsSearch } from "@/components/conversations/conversationsSearch";
import { defaultSamsApiBaseUrl } from "@/config";
import { HomeFeedPage } from "@/components/home-feed/HomeFeedPage";
import { AdminShell } from "@/components/layout/AdminShell";
import { useAdminShell } from "@/components/layout/AdminShellContext";
import { ProactiveMessagesPage } from "@/components/proactive-messages/ProactiveMessagesPage";
import { PromptLabPage } from "@/components/prompt-lab/PromptLabPage";
import { ChangelogPage } from "@/components/changelog/ChangelogPage";
import { SamsAdminsPage } from "@/components/sams-admins/SamsAdminsPage";
import { AllTestCasesPage } from "@/components/test-cases/AllTestCasesPage";
import { ResultSummary } from "@/components/test-result/ResultSummary";
import { ResultView } from "@/components/test-result/ResultView";
import { TestResultPage } from "@/components/test-result/TestResultPage";
import {
  TEST_RESULT_DEFAULT_SEARCH,
  TEST_RESULT_ROUTE,
  parseTestResultSearch,
} from "@/components/test-result/testResultSearch";
import {
  ALL_TEST_CASES_DEFAULT_SEARCH,
  parseAllTestCasesSearch,
} from "@/components/test-cases/allTestCasesSearch";
import { EmptyState } from "@/components/ui";
import { ReferenceAnswerPage } from "@/components/reference-answer/ReferenceAnswerPage";
import { CauseLoopTargetsPage } from "@/components/test-cases";
import { REFERENCE_ROUTE, parseReferenceSearch } from "@/components/reference-answer/referenceReview";

/**
 * Code-based route tree — no file-based routing, no `@tanstack/router-plugin`.
 * The whole tree is declared here, including screens that do not exist yet, so
 * that adding them later is filling in a component rather than reshaping the
 * tree. Spec: base_agent_spec/sams/TECH_SPEC_FE.md §4.1.
 *
 * Route components are thin adapters: they read `useAdminShell()` and hand the
 * same props to the same page components those pages already took. No page
 * component changed when routing landed.
 */

const NotFound = () => (
  <EmptyState
    title="여기에는 화면이 없습니다"
    body="주소를 확인해 주세요. 사이드바에서 다른 화면으로 이동할 수 있습니다."
  />
);

const rootRoute = createRootRoute({
  component: AdminShell,
  notFoundComponent: NotFound,
});

/**
 * Catch-all. Without it an unknown trailing segment resolves to the route
 * above it — `/agents/nope` renders Agents as if nothing were wrong, which is
 * worse than a 404 because the screen looks right. Real routes and their
 * params still win on specificity, so `/test-cases/batch/BT-99` keeps matching
 * the batch route and the missing row stays the component's problem.
 */
const splatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: NotFound,
});

/**
 * Opens on Agents, the section the shell selected before routing existed.
 *
 * The spec puts the landing screen at All Test Cases and eventually Dashboard
 * (TECH_SPEC_FE §4.1), but both are placeholders right now — landing on one
 * would make the app look broken on open. This moves when the screen is real.
 */
// --- Overview ------------------------------------------------------------

/**
 * Dashboard. `/` still lands on Agents: the landing moves here only once
 * scores exist to draw (fe/DASHBOARD.md §1) — before that the first screen
 * would be two empty cards.
 *
 * Loaded on first visit (it once carried the chart library).
 */
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "dashboard",
  component: lazyRouteComponent(() => import("@/components/dashboard/DashboardRoute"), "DashboardRoute"),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/agents" });
  },
});

// --- Agent ---------------------------------------------------------------

/**
 * Agents is two columns because the sidebar is navigation only now — the
 * agent picker lives with the screen it drives rather than under a nav item.
 * `AgentsPage` itself is unchanged; the list sits beside it.
 */
function AgentsRoute() {
  const shell = useAdminShell();
  return (
    <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="lg:sticky lg:top-5">
        <AgentList
          agents={shell.agents}
          selectedId={shell.selectedId}
          onSelect={shell.setSelectedId}
          api={shell.api}
        />
      </aside>
      <div className="min-w-0">
        <AgentsPage
          api={shell.api}
          agent={shell.selectedAgent}
          skillCatalog={shell.skillCatalog}
          saving={shell.saving}
          setSaving={shell.setSaving}
          onSaved={(next) => {
            shell.setAgents((items) =>
              items.map((item) =>
                agentIdentifier(item) === agentIdentifier(next) ? next : item,
              ),
            );
            shell.flash("Saved.");
          }}
          onError={shell.showError}
          onFlash={shell.flash}
        />
      </div>
    </div>
  );
}

const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "agents",
  component: AgentsRoute,
});

function PromptLabRoute() {
  const shell = useAdminShell();

  // Was `selectSection`'s load-on-first-open in the shell; it belongs to the
  // route now that the shell no longer knows which section is showing.
  useEffect(() => {
    if (!shell.promptState) void shell.loadPromptLab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PromptLabPage
      state={shell.promptState}
      api={shell.api}
          onFlash={shell.flash}
      onError={shell.showError}
      prodAccess={shell.samsAccess.state === "admin" && shell.samsAccess.me.prod_access === true}
    />
  );
}

const promptLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "prompt-lab",
  component: PromptLabRoute,
});

// --- Evaluation ----------------------------------------------------------

/**
 * Pathful, with relative children, so a route id reads like its URL
 * (`/test-cases/all`). A pathless parent carrying full-path children produced
 * `/test-cases/test-cases/all`, which every `from:` would have to repeat.
 *
 * No component: TanStack renders an `<Outlet/>` by default, and there is no
 * "Test Cases" page to land on — the tab is its screens.
 */
const testCasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "test-cases",
});

/**
 * `/test-cases` is a real section, so it opens its first screen rather than
 * 404ing. Without this it matches the layout route and renders an empty
 * Outlet — a blank page, which is the worst of the three options.
 */
const testCasesIndexRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/test-cases/all", search: ALL_TEST_CASES_DEFAULT_SEARCH });
  },
});

function AllTestCasesRoute() {
  const shell = useAdminShell();
  return <AllTestCasesPage api={shell.api} reloadKey={shell.reloadKey} />;
}

const allTestCasesRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "all",
  component: AllTestCasesRoute,
  // Normalizes rather than validates: a pasted link, an old bookmark or a
  // hand-edited address lands on a working screen instead of throwing
  // (fe/TEST_CASES_ALL.md §5).
  validateSearch: parseAllTestCasesSearch,
});

function BatchTestRoute() {
  const shell = useAdminShell();
  return <BatchTestListPage api={shell.api} reloadKey={shell.reloadKey} />;
}

function ReferenceAnswerRoute() {
  const shell = useAdminShell();
  const access = shell.samsAccess;
  return (
    <ReferenceAnswerPage
      api={shell.api}
      reloadKey={shell.reloadKey}
      prodAccess={access.state === "admin" ? access.me.prod_access === true : access.state === "loading" ? null : false}
    />
  );
}

const referenceAnswerRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "references",
  component: ReferenceAnswerRoute,
  validateSearch: parseReferenceSearch,
});

function CauseLoopTargetsRoute() {
  const shell = useAdminShell();
  const navigate = useNavigate();
  return (
    <CauseLoopTargetsPage
      api={shell.api}
      // A row opens its TC on the Reference review tab (LOOPFLOW H-6).
      onOpen={(testCaseId) => void navigate({ to: REFERENCE_ROUTE, search: { view: "review", tc: testCaseId, snap: null } })}
      onOpenResult={(testCaseId) => void navigate({ to: TEST_RESULT_ROUTE, search: { ...TEST_RESULT_DEFAULT_SEARCH, tc: testCaseId } })}
    />
  );
}

const causeLoopTargetsRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "cause-loop",
  component: CauseLoopTargetsRoute,
});

function CandidatesRoute() {
  const shell = useAdminShell();
  return <ProblemIdentificationPage api={shell.api} access={shell.samsAccess} />;
}

const candidatesRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "candidates",
  component: CandidatesRoute,
});

const batchTestRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "batch",
  component: BatchTestRoute,
});

function BatchPageRoute() {
  const shell = useAdminShell();
  const { pageId } = batchPageRoute.useParams();
  // Keyed by page so moving between pages starts from a clean slate — the
  // picker's draft and the TE editor belong to one page.
  return <BatchPageView key={pageId} api={shell.api} pageId={pageId} reloadKey={shell.reloadKey} />;
}

const batchPageRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "batch/$pageId",
  component: BatchPageRoute,
});

/**
 * Test Result — its own tab, as in the artifact: Batch Test starts runs,
 * this reads them. The Matrix carries its page and filters in the search
 * (fe/TEST_CASES_RESULT.md §2).
 */
function TestResultRoute() {
  const shell = useAdminShell();
  return <TestResultPage api={shell.api} reloadKey={shell.reloadKey} />;
}

const testResultRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "result",
  component: TestResultRoute,
  validateSearch: parseTestResultSearch,
});

/**
 * One cell: a TC under a run. The run is the address, not the page or a TE
 * letter — a cell is exactly one run, runs outlive their page, and letters
 * are only the page's way of naming them.
 */
function ResultDetailRoute() {
  const shell = useAdminShell();
  const { runId, tcId } = resultDetailRoute.useParams();
  return (
    <ResultView
      key={`${runId}/${tcId}`}
      api={shell.api}
      runId={runId}
      testCaseId={tcId}
      reloadKey={shell.reloadKey}
    />
  );
}

const resultDetailRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "result/runs/$runId/$tcId",
  component: ResultDetailRoute,
});

function ResultSummaryRoute() {
  const shell = useAdminShell();
  const { pageId } = resultSummaryRoute.useParams();
  return <ResultSummary key={pageId} api={shell.api} pageId={pageId} reloadKey={shell.reloadKey} />;
}

const resultSummaryRoute = createRoute({
  getParentRoute: () => testCasesRoute,
  path: "result/pages/$pageId/summary",
  component: ResultSummaryRoute,
});

// Conversations and both engagement pages own their own state and fetching —
// the shell is already carrying agents/prompts and doesn't
// need more datasets.
function ConversationsRoute() {
  const shell = useAdminShell();
  return (
    <ConversationsPage
      apiBaseUrl={shell.apiBaseUrl}
      bearerToken={shell.session.access_token}
      samsApiBaseUrl={defaultSamsApiBaseUrl}
    />
  );
}

const conversationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "conversations",
  component: ConversationsRoute,
  // `?env&session&message` — a trace is addressable, which is what lets the
  // TC drawer link straight to the answer it came from (fe/CONVERSATIONS.md).
  validateSearch: parseConversationsSearch,
});

// --- Engagement ----------------------------------------------------------

function ProactiveMessagesRoute() {
  const shell = useAdminShell();
  return (
    <ProactiveMessagesPage
      apiBaseUrl={shell.apiBaseUrl}
      bearerToken={shell.session.access_token}
      samsApiBaseUrl={defaultSamsApiBaseUrl}
    />
  );
}

const proactiveMessagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "proactive-messages",
  component: ProactiveMessagesRoute,
});

function HomeFeedRoute() {
  const shell = useAdminShell();
  return (
    <HomeFeedPage
      apiBaseUrl={shell.apiBaseUrl}
      bearerToken={shell.session.access_token}
      samsApiBaseUrl={defaultSamsApiBaseUrl}
    />
  );
}

const homeFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "home-feed",
  component: HomeFeedRoute,
});

// --- Settings ------------------------------------------------------------

/**
 * SAMS admin management. Shown only to an admin SAMS confirmed through
 * `/admin/sams/admins/me` (the shell asks once); anyone else gets the reason
 * instead of the screen. The server still decides every call.
 */
function AdminsRoute() {
  const shell = useAdminShell();
  const access = shell.samsAccess;
  if (access.state === "loading") {
    return <p className="text-[13px] text-muted-foreground">SAMS 권한을 확인하는 중…</p>;
  }
  if (access.state === "denied") {
    return <EmptyState title="SAMS 관리자만 볼 수 있습니다" body={access.reason} />;
  }
  return <SamsAdminsPage api={shell.api} me={access.me} onAccessChanged={shell.refreshSamsAccess} />;
}

const adminsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admins",
  component: AdminsRoute,
});

function ChangelogRoute() {
  const shell = useAdminShell();
  const access = shell.samsAccess;
  if (access.state === "loading") return <p className="text-[13px] text-muted-foreground">SAMS 권한을 확인하는 중…</p>;
  if (access.state === "denied") return <EmptyState title="SAMS 관리자만 볼 수 있습니다" body={access.reason} />;
  return <ChangelogPage key={access.me.prod_access ? "prod" : "dev"} api={shell.api} />;
}

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "changelog",
  component: ChangelogRoute,
});

/** The model catalog, loaded on first visit (`ModelCatalogRoute` gates it to SAMS admins). */
const modelCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "models",
  component: lazyRouteComponent(() => import("@/components/model-catalog/ModelCatalogRoute"), "ModelCatalogRoute"),
});

/** The call-site screen, loaded on first visit (`CallSitesRoute` gates it to SAMS admins; `?key=` opens a site). */
const callSitesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "call-sites",
  component: lazyRouteComponent(() => import("@/components/call-sites/CallSitesRoute"), "CallSitesRoute"),
});

/** Backend tool errors, loaded on first visit (`BackendErrorsRoute` gates it to SAMS admins). */
const backendErrorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "backend-errors",
  component: lazyRouteComponent(() => import("@/components/backend-errors/BackendErrorsRoute"), "BackendErrorsRoute"),
});

// -------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  agentsRoute,
  promptLabRoute,
  testCasesRoute.addChildren([
    testCasesIndexRoute,
    allTestCasesRoute,
    candidatesRoute,
    batchTestRoute,
    batchPageRoute,
    testResultRoute,
    resultDetailRoute,
    resultSummaryRoute,
    referenceAnswerRoute,
    causeLoopTargetsRoute,
  ]),
  conversationsRoute,
  proactiveMessagesRoute,
  homeFeedRoute,
  adminsRoute,
  modelCatalogRoute,
  callSitesRoute,
  changelogRoute,
  backendErrorsRoute,
  splatRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
